import { createContext, useContext, useState, type ReactNode } from 'react';

/**
 * How the Shared Docs tree is showing, if at all.
 *
 * Docs mode is a single column -- the list, or the document -- and the tree
 * is not part of it by default. It comes back in two presentations:
 *
 * - **Docked.** The default at desktop widths, as the desktop app keeps its
 *   Pages tree open: a pane beside the content, resizable, until it is hidden
 *   (and docked again from the breadcrumb toggle). The choice is remembered.
 * - **Fly-over.** Re-clicking the Docs gutter item while already in Docs
 *   slides the tree over the content for one pick, the way a navigation
 *   drawer does, and it goes away once used. Never remembered.
 *
 * The two triggers live in different subtrees (the gutter and the docs
 * shell are siblings), so the state sits above both, in the app shell.
 */
export interface DocsTreeState {
  docked: boolean;
  setDocked: (docked: boolean) => void;
  flyoverOpen: boolean;
  setFlyoverOpen: (open: boolean) => void;
}

const DocsTreeContext = createContext<DocsTreeState | null>(null);

export function DocsTreeProvider({ value, children }: { value: DocsTreeState; children: ReactNode }) {
  return <DocsTreeContext.Provider value={value}>{children}</DocsTreeContext.Provider>;
}

/**
 * The tree state, or a local one when nothing above provides it -- the
 * verification harness mounts the docs shell with no app shell around it,
 * and its breadcrumb toggle should still work there.
 */
export function useDocsTree(): DocsTreeState {
  const provided = useContext(DocsTreeContext);
  const [docked, setDocked] = useState(false);
  const [flyoverOpen, setFlyoverOpen] = useState(false);
  return provided ?? { docked, setDocked, flyoverOpen, setFlyoverOpen };
}

interface DocsTreeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const DOCKED_STORAGE_KEY = 'nimbalyst:web-console:docs-tree-docked';

function browserStorage(): DocsTreeStorage {
  return window.localStorage;
}

/** Whether the tree is docked: on until the reader hides it. */
export function readDocsTreeDocked(getStorage: () => DocsTreeStorage = browserStorage): boolean {
  try {
    return getStorage().getItem(DOCKED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function rememberDocsTreeDocked(docked: boolean, getStorage: () => DocsTreeStorage = browserStorage): void {
  try {
    getStorage().setItem(DOCKED_STORAGE_KEY, String(docked));
  } catch {
    // The in-memory state still works when site data is unavailable.
  }
}
